/* eslint-disable no-empty */

/* eslint-disable no-param-reassign */

/* eslint-disable @typescript-eslint/no-shadow */
import { RangePickerValueType } from '@sede-enterprise/shell-ds-react-framework';

import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { ColumnFiltersState } from '@tanstack/react-table';
import dayjs from 'dayjs';
import { API_URLS } from '../../constants/ApiUrls';
import { eosGet, eosPost } from '../../services/EosApi';
import DATEFORMAT from '../../utils/date-format';
import { EnergyUnit } from '../../utils/energyConversion';
import { getAxiosErrorMessage } from '../../utils/general';
import { IDashboardEOSState } from '../types/dashboard.types';
import {
  ForecastAnalysisPayload,
  ForecastAnalysisResponse,
  ForecastFileOption,
  ForecastFileWithDates,
  IForecastAnalysisState,
  ISaveAuditInfoPayload,
  LatestUploadedForecast,
  SaveAuditLogInfoResponse,
  UKForecastGasDayResponse,
} from '../types/forecast.types';
import { ReportTypeOption } from '../types/reportTypes.types';

type ForecastAnalysisStateShape = {
  dashboardEOS: IDashboardEOSState;
  forecastAnalysis: IForecastAnalysisState;
};

const initialState: IForecastAnalysisState = {
  selectedForecastType: null,
  selectedForecastFile: null,
  dateRange: [null, null],
  appliedDateRange: [null, null],
  selectedEnergyUnit: 'Therms',
  selectedFieldIds: [],
  selectedDeliveryPointIds: [],
  columnFilters: [],

  forecastAnalysis: {
    loading: false,
    data: null,
    error: null,
  },

  forecastFiles: {
    loading: false,
    data: [],
    error: null,
    isNewFilePresent: false,
  },

  deliveryPointOptions: {
    loading: false,
    data: [],
    error: null,
  },

  fieldOptions: {
    loading: false,
    data: [],
    error: null,
  },

  latestUploadedForecast: {
    loading: false,
    data: null,
    error: null,
  },
};

const MISSING_PARAMETERS_ERROR = 'Missing required parameters';
const UNKNOWN_ERROR = 'Unknown error occurred';

export const fetchForecastAnalysisData = createAsyncThunk<
  ForecastAnalysisResponse | UKForecastGasDayResponse,
  ForecastAnalysisPayload
>(
  'forecastAnalysis/fetchForecastAnalysisData',
  async (payload: ForecastAnalysisPayload, { rejectWithValue, getState }) => {
    const state = getState() as ForecastAnalysisStateShape;
    const { selectedPortfolioId } = state.dashboardEOS.portfolio;
    const { selectedCountryId } = state.dashboardEOS.scenarioFilters;
    const { selectedFieldIds } = state.forecastAnalysis;
    const { selectedDeliveryPointIds } = state.forecastAnalysis;

    const updatedPayload = {
      ...payload,
      portfolioId: selectedPortfolioId,
      marketId: selectedCountryId,
      ...(selectedFieldIds.length > 0 && {
        fieldId: selectedFieldIds.join(','),
      }),
      ...(selectedDeliveryPointIds.length > 0 && {
        deliveryPointId: selectedDeliveryPointIds.join(','),
      }),
    };

    try {
      const response = await eosPost(API_URLS.FORECAST_DATA, updatedPayload);
      const isUKMarket = Number(selectedCountryId) === 1;
      return isUKMarket
        ? (response.data as UKForecastGasDayResponse)
        : (response.data?.[0] as ForecastAnalysisResponse);
    } catch (error: unknown) {
      const errorMessage = getAxiosErrorMessage(error, 'Failed to fetch forecast analysis data');
      return rejectWithValue(errorMessage);
    }
  },
);

export const saveAuditLogInfo = createAsyncThunk<SaveAuditLogInfoResponse, ISaveAuditInfoPayload>(
  'forecastAnalysis/saveAuditLogInfo',
  async (payload: ISaveAuditInfoPayload, { rejectWithValue }) => {
    try {
      const response = await eosPost(API_URLS.AUDIT_SAVE_AUDIT_LOG_DATA, payload);
      return response.data;
    } catch (error: unknown) {
      const errorMessage = getAxiosErrorMessage(error, 'Failed to save audit log info');
      return rejectWithValue(errorMessage);
    }
  },
);

export const fetchForecastFiles = createAsyncThunk(
  'scenarioAnalysis/fetchForecastFiles',
  async (supplyContractId: number | null, { rejectWithValue, getState, signal }) => {
    const state = getState() as ForecastAnalysisStateShape;
    const { selectedPortfolioId } = state.dashboardEOS.portfolio;
    const { selectedCountryId } = state.dashboardEOS.scenarioFilters;
    if (!supplyContractId || !selectedPortfolioId || !selectedCountryId) {
      return rejectWithValue(MISSING_PARAMETERS_ERROR);
    }

    try {
      const { data } = await eosGet(
        `${API_URLS.EOS_FORECAST_FILE_NAME}?supplyContractId=${supplyContractId}&marketId=${selectedCountryId}&portfolioId=${selectedPortfolioId}`,
        { signal },
      );

      const forecastFileOptions: ForecastFileOption[] = data?.map(
        (file: {
          fileName: string;
          forecastType: string;
          startDate: string;
          endDate: string;
          isNewFile: boolean;
        }) => ({
          name: file.fileName,
          id: file.fileName,
          value: file.fileName,
          forecastType: file.forecastType,
          startDate: file.startDate,
          endDate: file.endDate,
          isNewFile: file.isNewFile,
        }),
      );
      return forecastFileOptions.map((file: ForecastFileOption, index: number) => ({
        ...file,
        id: index + 1,
      }));
    } catch (error: unknown) {
      const errorMessage = getAxiosErrorMessage(error, UNKNOWN_ERROR);
      return rejectWithValue(errorMessage);
    }
  },
);

export const fetchLatestUploadedForecast = createAsyncThunk<LatestUploadedForecast>(
  'forecastAnalysis/fetchLatestUploadedForecast',
  async (_, { rejectWithValue, getState }) => {
    const state = getState() as ForecastAnalysisStateShape;
    const { selectedPortfolioId } = state.dashboardEOS.portfolio;
    const { selectedCountryId, selectedContractId } = state.dashboardEOS.scenarioFilters;
    const { selectedForecastFile } = state.forecastAnalysis;

    if (
      !selectedPortfolioId ||
      !selectedCountryId ||
      !selectedContractId ||
      !selectedForecastFile?.name
    ) {
      return rejectWithValue(MISSING_PARAMETERS_ERROR);
    }

    try {
      const { data } = await eosGet(
        `${API_URLS.FORECAST_LATEST_UPLOADED}?portfolioId=${selectedPortfolioId}&marketId=${selectedCountryId}&supplyContractId=${selectedContractId}&fileName=${selectedForecastFile.name}`,
      );

      return data as LatestUploadedForecast;
    } catch (error: unknown) {
      const errorMessage = getAxiosErrorMessage(error, 'Failed to fetch latest uploaded forecast');
      return rejectWithValue(errorMessage);
    }
  },
);

export const fetchDeliveryPointOptions = createAsyncThunk(
  'forecastAnalysis/fetchDeliveryPointOptions',
  async (_, { rejectWithValue, getState, signal }) => {
    const state = getState() as ForecastAnalysisStateShape;

    const { selectedPortfolioId } = state.dashboardEOS.portfolio;

    const { selectedCountryId, selectedContractId } = state.dashboardEOS.scenarioFilters;

    if (!selectedPortfolioId || !selectedCountryId || !selectedContractId) {
      return rejectWithValue(MISSING_PARAMETERS_ERROR);
    }

    try {
      const { data } = await eosGet(
        `${API_URLS.MAPPING_GET_DELIVERY_POINT_MAPPINGS}?portfolioId=${selectedPortfolioId}&supplyContractId=${selectedContractId}`,
        { signal },
      );

      return data.map((item: { deliveryPoint: string; deliveryPointId: number }) => ({
        label: item.deliveryPoint,
        value: item.deliveryPointId,
      }));
    } catch (error: unknown) {
      const errorMessage = getAxiosErrorMessage(error, UNKNOWN_ERROR);
      return rejectWithValue(errorMessage);
    }
  },
);

export const fetchFieldOptions = createAsyncThunk(
  'forecastAnalysis/fetchFieldOptions',
  async (_, { rejectWithValue, getState, signal }) => {
    const state = getState() as ForecastAnalysisStateShape;

    const { selectedPortfolioId } = state.dashboardEOS.portfolio;

    const { selectedCountryId, selectedContractId } = state.dashboardEOS.scenarioFilters;

    if (!selectedPortfolioId || !selectedCountryId || !selectedContractId) {
      return rejectWithValue(MISSING_PARAMETERS_ERROR);
    }

    try {
      const { data } = await eosGet(
        `${API_URLS.MAPPING_GET_FIELD_MAPPINGS}?portfolioId=${selectedPortfolioId}&supplyContractId=${selectedContractId}`,
        { signal },
      );

      return data.map((item: { field: string; fieldId: number }) => ({
        label: item.field,
        value: item.fieldId,
      }));
    } catch (error: unknown) {
      const errorMessage = getAxiosErrorMessage(error, UNKNOWN_ERROR);
      return rejectWithValue(errorMessage);
    }
  },
);

export const forecastAnalysisSlice = createSlice({
  name: 'forecastAnalysis',
  initialState,
  reducers: {
    setSelectedForecastType: (state, action: PayloadAction<ReportTypeOption | null>) => {
      state.selectedForecastType = action.payload;
      state.selectedForecastFile = null;
      state.dateRange = [null, null];
      state.appliedDateRange = [null, null];
      // Reset any prior fetch result/error so a stale 400 does not persist across selection changes
      state.forecastAnalysis.data = null;
      state.forecastAnalysis.error = null;
    },
    setSelectedForecastFile: (state, action: PayloadAction<ForecastFileWithDates | null>) => {
      state.selectedForecastFile = action.payload;
      // Reset any prior fetch result/error so a stale 400 does not persist across file changes
      state.forecastAnalysis.data = null;
      state.forecastAnalysis.error = null;
    },
    setDateRange: (state, action: PayloadAction<RangePickerValueType>) => {
      state.dateRange = action.payload;
    },
    applyDateRange: (state) => {
      state.appliedDateRange = state.dateRange;
    },
    setSelectedEnergyUnit: (state, action: PayloadAction<EnergyUnit>) => {
      state.selectedEnergyUnit = action.payload;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.forecastAnalysis.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.forecastAnalysis.error = action.payload;
    },
    clearAnalysisData: (state) => {
      state.forecastAnalysis.data = null;
      state.forecastAnalysis.error = null;
    },
    clearLatestUploadedForecast: (state) => {
      state.latestUploadedForecast.data = null;
      state.latestUploadedForecast.error = null;
      state.latestUploadedForecast.loading = false;
    },
    setSelectedFieldIds: (state, action: PayloadAction<number[]>) => {
      state.selectedFieldIds = action.payload;
    },
    setSelectedDeliveryPointIds: (state, action: PayloadAction<number[]>) => {
      state.selectedDeliveryPointIds = action.payload;
    },
    setColumnFilters: (state, action: PayloadAction<ColumnFiltersState>) => {
      state.columnFilters = action.payload;
    },
    onunmount: (state) => {
      state.selectedForecastType = null;
      state.selectedForecastFile = null;
      state.dateRange = [null, null];
      state.appliedDateRange = [null, null];
      state.selectedEnergyUnit = 'Therms';
      state.selectedFieldIds = [];
      state.selectedDeliveryPointIds = [];
      state.columnFilters = [];
      state.latestUploadedForecast = {
        loading: false,
        data: null,
        error: null,
      };
      state.forecastAnalysis = {
        loading: false,
        data: null,
        error: null,
      };
      state.forecastFiles = {
        loading: false,
        data: [],
        error: null,
        isNewFilePresent: false,
      };
      state.deliveryPointOptions = {
        loading: false,
        data: [],
        error: null,
      };
      state.fieldOptions = {
        loading: false,
        data: [],
        error: null,
      };
    },
    resetState: () => {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder

      .addCase(fetchForecastFiles.pending, (state) => {
        state.forecastFiles.loading = true;
        state.forecastFiles.error = null;
      })
      .addCase(fetchForecastFiles.fulfilled, (state, action) => {
        state.forecastFiles.data = action.payload ?? [];
        state.forecastFiles.loading = false;
        // check if any of the files in the payload are new and update the state accordingly
        state.forecastFiles.isNewFilePresent = state.forecastFiles.data.some(
          (file) => file.isNewFile === true,
        );
      })
      .addCase(fetchForecastFiles.rejected, (state, action) => {
        state.forecastFiles.error = action.error.message ?? null;
        state.forecastFiles.loading = false;
        state.forecastFiles.isNewFilePresent = false;
      })
      // Forecast Analysis Data
      .addCase(fetchForecastAnalysisData.pending, (state) => {
        state.forecastAnalysis.loading = true;
        state.forecastAnalysis.error = null;
      })
      .addCase(fetchForecastAnalysisData.fulfilled, (state, action) => {
        state.forecastAnalysis.loading = false;
        state.forecastAnalysis.error = null;
        state.forecastAnalysis.data = action.payload;
      })
      .addCase(fetchForecastAnalysisData.rejected, (state, action) => {
        state.forecastAnalysis.loading = false;
        state.forecastAnalysis.error = action.payload as string;
        state.forecastAnalysis.data = null;
      })
      // Delivery Point Mappings
      .addCase(fetchDeliveryPointOptions.pending, (state) => {
        state.deliveryPointOptions.loading = true;
        state.deliveryPointOptions.error = null;
      })
      .addCase(fetchDeliveryPointOptions.fulfilled, (state, action) => {
        state.deliveryPointOptions.data = action.payload ?? [];
        state.deliveryPointOptions.loading = false;
      })
      .addCase(fetchDeliveryPointOptions.rejected, (state, action) => {
        state.deliveryPointOptions.error = action.error.message ?? null;
        state.deliveryPointOptions.loading = false;
      })
      // Field Mappings
      .addCase(fetchFieldOptions.pending, (state) => {
        state.fieldOptions.loading = true;
        state.fieldOptions.error = null;
      })
      .addCase(fetchFieldOptions.fulfilled, (state, action) => {
        state.fieldOptions.data = action.payload ?? [];
        state.fieldOptions.loading = false;
      })
      .addCase(fetchFieldOptions.rejected, (state, action) => {
        state.fieldOptions.error = action.error.message ?? null;
        state.fieldOptions.loading = false;
      })
      // Latest uploaded forecast
      .addCase(fetchLatestUploadedForecast.pending, (state) => {
        state.latestUploadedForecast.loading = true;
        state.latestUploadedForecast.error = null;
      })
      .addCase(fetchLatestUploadedForecast.fulfilled, (state, action) => {
        state.latestUploadedForecast.data = action.payload ?? null;
        state.latestUploadedForecast.loading = false;
      })
      .addCase(fetchLatestUploadedForecast.rejected, (state, action) => {
        state.latestUploadedForecast.error = action.payload as string;
        state.latestUploadedForecast.loading = false;
        state.latestUploadedForecast.data = null;
      });
  },
});

export const getForecastFilesState = (state: ForecastAnalysisStateShape) =>
  state.forecastAnalysis.forecastFiles;

export const getForecastAnalysisState = (state: ForecastAnalysisStateShape) =>
  state.forecastAnalysis.forecastAnalysis;

export const getSelectedForecastAnalysisData = (state: ForecastAnalysisStateShape) =>
  state.forecastAnalysis;

export const getDeliveryPointOptionsState = (state: ForecastAnalysisStateShape) =>
  state.forecastAnalysis.deliveryPointOptions;

export const getFieldOptionsState = (state: ForecastAnalysisStateShape) =>
  state.forecastAnalysis.fieldOptions;

export const getSelectedFieldIds = (state: ForecastAnalysisStateShape) =>
  state.forecastAnalysis.selectedFieldIds;

export const getSelectedDeliveryPointIds = (state: ForecastAnalysisStateShape) =>
  state.forecastAnalysis.selectedDeliveryPointIds;

export const getLatestUploadedForecastState = (state: ForecastAnalysisStateShape) =>
  state.forecastAnalysis.latestUploadedForecast;

export const forecastAnalysisActions = forecastAnalysisSlice.actions;

/**
 * Orchestrates a forecast-table filter change.
 *
 * Reads the latest selections from Redux via `getState()` (the idiomatic place
 * for cross-slice reads), so the table's `onFetchData` callback in the
 * component can stay stable and free of stale-closure problems.
 */
export const applyForecastTableFilters = createAsyncThunk<void, { filters: ColumnFiltersState }>(
  'forecastAnalysis/applyForecastTableFilters',
  async ({ filters }, { dispatch, getState }) => {
    dispatch(forecastAnalysisSlice.actions.setColumnFilters(filters));
    const state = getState() as ForecastAnalysisStateShape;
    const { selectedForecastFile, dateRange, selectedForecastType } = state.forecastAnalysis;
    const { selectedContractId } = state.dashboardEOS.scenarioFilters;
    const hasRequiredSelections =
      !!selectedForecastFile && !!selectedContractId && !!selectedForecastType;
    const hasDateRange = !!(dateRange?.[0] && dateRange?.[1]);

    if (!hasRequiredSelections || !hasDateRange) {
      return;
    }

    const startDate = dayjs(dateRange[0]).format(DATEFORMAT.yearMonthDay);
    const endDate = dayjs(dateRange[1]).format(DATEFORMAT.yearMonthDay);

    const processedFilters = Object.fromEntries(
      filters.map((filter) => [
        filter.id,
        Array.isArray(filter.value) ? filter.value.join(',') : String(filter.value),
      ]),
    );

    const deliveryPointFilter = filters.find((f) => f.id === 'deliveryPointId');
    dispatch(
      forecastAnalysisSlice.actions.setSelectedDeliveryPointIds(
        (deliveryPointFilter?.value as number[]) || [],
      ),
    );

    dispatch(
      fetchForecastAnalysisData({
        startDate,
        endDate,
        supplyContractId: selectedContractId,
        forecastTypeId: selectedForecastType.id,
        fileName: selectedForecastFile.name,
        ...processedFilters,
      }),
    );
  },
);

export const resetForecastHaircut = createAsyncThunk<
  void,
  Pick<ForecastAnalysisPayload, 'pageNumber' | 'pageSize'> | undefined
>(
  'forecastAnalysis/resetForecastHaircut',
  async (pagination, { dispatch, rejectWithValue, getState }) => {
    const state = getState() as ForecastAnalysisStateShape;
    const { selectedPortfolioId } = state.dashboardEOS.portfolio;
    const { selectedCountryId, selectedContractId } = state.dashboardEOS.scenarioFilters;
    const {
      selectedForecastFile,
      selectedForecastType,
      dateRange,
      selectedEnergyUnit,
      selectedFieldIds,
      selectedDeliveryPointIds,
    } = state.forecastAnalysis;
    const hasDateRange = !!(dateRange?.[0] && dateRange?.[1]);
    const hasRequiredSelections =
      !!selectedContractId && !!selectedForecastFile && !!selectedForecastType;
    const hasRequiredContext = !!selectedPortfolioId && !!selectedCountryId;

    if (!hasRequiredSelections || !hasRequiredContext || !hasDateRange) {
      return rejectWithValue(MISSING_PARAMETERS_ERROR);
    }

    const startDate = dayjs(dateRange[0]).format(DATEFORMAT.yearMonthDay);
    const endDate = dayjs(dateRange[1]).format(DATEFORMAT.yearMonthDay);

    try {
      await eosPost(API_URLS.FORECAST_RESET_HAIRCUT_DATA, {
        startDate,
        endDate,
        supplyContractId: selectedContractId,
        forecastTypeId: selectedForecastType.id,
        fileName: selectedForecastFile.name,
        haircutPercentage: null,
        fiscalityPerDay: null,
        uom: selectedEnergyUnit,
        portfolioId: selectedPortfolioId,
        marketId: selectedCountryId,
        fieldId: selectedFieldIds.length > 0 ? selectedFieldIds.join(',') : 'All',
        deliveryPointId:
          selectedDeliveryPointIds.length > 0 ? selectedDeliveryPointIds.join(',') : 'All',
      });

      dispatch(
        fetchForecastAnalysisData({
          ...pagination,
          startDate,
          endDate,
          supplyContractId: selectedContractId,
          forecastTypeId: selectedForecastType.id,
          fileName: selectedForecastFile.name,
        }),
      );
      return undefined;
    } catch (error: unknown) {
      const errorMessage = getAxiosErrorMessage(error, 'Failed to reset haircut');
      return rejectWithValue(errorMessage);
    }
  },
);

export const resetForecastFiscality = createAsyncThunk<void>(
  'forecastAnalysis/resetForecastFiscality',
  async (_, { dispatch, rejectWithValue, getState }) => {
    const state = getState() as ForecastAnalysisStateShape;
    const { selectedPortfolioId } = state.dashboardEOS.portfolio;
    const { selectedCountryId, selectedContractId } = state.dashboardEOS.scenarioFilters;
    const {
      selectedForecastFile,
      selectedForecastType,
      dateRange,
      selectedEnergyUnit,
      selectedFieldIds,
      selectedDeliveryPointIds,
    } = state.forecastAnalysis;
    const hasDateRange = !!(dateRange?.[0] && dateRange?.[1]);
    const hasRequiredSelections =
      !!selectedContractId && !!selectedForecastFile && !!selectedForecastType;
    const hasRequiredContext = !!selectedPortfolioId && !!selectedCountryId;

    if (!hasRequiredSelections || !hasRequiredContext || !hasDateRange) {
      return rejectWithValue(MISSING_PARAMETERS_ERROR);
    }

    const startDate = dayjs(dateRange[0]).format(DATEFORMAT.yearMonthDay);
    const endDate = dayjs(dateRange[1]).format(DATEFORMAT.yearMonthDay);

    try {
      await eosPost(API_URLS.FORECAST_RESET_FISCALITY_DATA, {
        startDate,
        endDate,
        supplyContractId: selectedContractId,
        forecastTypeId: selectedForecastType.id,
        fileName: selectedForecastFile.name,
        haircutPercentage: null,
        fiscalityPerDay: null,
        uom: selectedEnergyUnit,
        portfolioId: selectedPortfolioId,
        marketId: selectedCountryId,
        fieldId: selectedFieldIds.length > 0 ? selectedFieldIds.join(',') : 'All',
        deliveryPointId:
          selectedDeliveryPointIds.length > 0 ? selectedDeliveryPointIds.join(',') : 'All',
      });

      dispatch(
        fetchForecastAnalysisData({
          startDate,
          endDate,
          supplyContractId: selectedContractId,
          forecastTypeId: selectedForecastType.id,
          fileName: selectedForecastFile.name,
        }),
      );
      return undefined;
    } catch (error: unknown) {
      const errorMessage = getAxiosErrorMessage(error, 'Failed to reset fiscality');
      return rejectWithValue(errorMessage);
    }
  },
);

export default forecastAnalysisSlice.reducer;
